/**
 * NNH Add-to-Cart — single shared module for all PDP templates.
 *
 * Contract
 * --------
 * Any button with [data-nnh-atc] inside (or referencing) a form with
 * [data-nnh-form] triggers a cart add. The form must contain:
 *   <input data-nnh-variant-id>    — Shopify variant ID (kept in sync by template JS)
 *   <input data-nnh-selling-plan>  — selling plan ID, blank for one-time (optional)
 *   <input data-nnh-quantity>      — quantity, defaults to 1 (optional)
 *   <input data-nnh-compare-at>    — compare-at price in cents for cart property (optional)
 *
 * Templates that need custom variant resolution (Universal PDP) call
 * window.NNHCart.addItem() / window.NNHCart.addViaSkio() directly.
 *
 * Public API: window.NNHCart = { addItem, addViaSkio, openDrawer, showToast }
 */
(function (win, doc) {
  'use strict';
  if (win.NNHCart) return; // guard against double-load

  function _getCookie(name) {
    var match = doc.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
    return match ? decodeURIComponent(match[1]) : '';
  }

  // ── Toast ─────────────────────────────────────────────────────────────────
  var _toast = null;
  var _toastTimer = null;

  function showToast(msg, type) {
    if (!_toast) {
      _toast = doc.createElement('div');
      _toast.id = 'nnh-toast';
      _toast.setAttribute('role', 'alert');
      _toast.setAttribute('aria-live', 'assertive');
      _toast.style.cssText = [
        'position:fixed', 'bottom:24px', 'left:50%',
        'transform:translateX(-50%) translateY(0)',
        'padding:12px 22px', 'border-radius:8px',
        'font-size:14px', 'font-family:inherit',
        'z-index:99999', 'box-shadow:0 4px 16px rgba(0,0,0,.18)',
        'max-width:90vw', 'text-align:center',
        'pointer-events:none', 'transition:opacity .3s ease',
        'opacity:0'
      ].join(';');
      doc.body.appendChild(_toast);
    }
    clearTimeout(_toastTimer);
    _toast.style.background = type === 'error' ? '#b91c1c' : '#166534';
    _toast.style.color = '#fff';
    _toast.textContent = msg;
    _toast.style.opacity = '1';
    _toastTimer = setTimeout(function () {
      _toast.style.opacity = '0';
    }, 3800);
  }

  // ── Cart drawer — multi-fallback ──────────────────────────────────────────
  function openDrawer() {
    // 1. Rebuy SmartCart — direct call, no extra network request
    if (win.Rebuy && win.Rebuy.SmartCart && typeof win.Rebuy.SmartCart.show === 'function') {
      win.Rebuy.SmartCart.show();
      return;
    }
    // 2. BoosterTheme
    if (typeof BoosterTheme !== 'undefined' && BoosterTheme.cart &&
        typeof BoosterTheme.cart.open === 'function') {
      BoosterTheme.cart.open();
      return;
    }
    // 4. Generic openCartDrawer (registered by add-to-cart-handler.liquid)
    if (typeof win.openCartDrawer === 'function') {
      win.openCartDrawer();
      return;
    }
    // 5. Click the first available cart trigger
    var trigger = doc.querySelector(
      '[data-cart-icon],[aria-controls="cart-drawer"],.js-cart-link,a[href="/cart"]'
    );
    if (trigger) { trigger.click(); return; }
    // 6. Wait for BoosterTheme init event (fires after async JS loads)
    doc.addEventListener('booster:initialized', function handler(e) {
      doc.removeEventListener('booster:initialized', handler);
      var bt = e.detail && e.detail.BoosterTheme;
      if (bt && bt.cart && typeof bt.cart.open === 'function') bt.cart.open();
    });
  }

  // ── Dispatch standard cart events ─────────────────────────────────────────
  function _dispatchEvents(variantId, qty, data) {
    doc.dispatchEvent(new CustomEvent('cart:item-added', {
      bubbles: true,
      detail: { variantId: variantId, quantity: qty, data: data }
    }));
    doc.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
      bubbles: true,
      detail: { source: 'nnh-atc' }
    }));
  }

  // ── Button helpers ────────────────────────────────────────────────────────
  function _textEl(btn) {
    return btn && btn.querySelector(
      '.sticky-atc-text,[data-cta-label],[data-button-text],.btn-cta__text,[data-pcos-sticky-cta-label]'
    );
  }

  /** Modular / PCOS hero: primary [data-nnh-atc] + floating bar share data-nnh-atc-sync="{{ section.id }}" */
  function _buttonsInAtcSyncGroup(primaryBtn) {
    if (!primaryBtn) return [];
    var gid = primaryBtn.getAttribute('data-nnh-atc-sync');
    if (!gid) return [primaryBtn];
    return Array.prototype.slice.call(doc.querySelectorAll('[data-nnh-atc-sync="' + gid + '"]'));
  }

  function _isIconBtn(btn) {
    return !!(btn && btn.hasAttribute('data-nnh-atc-icon'));
  }

  function getBtnText(btn) {
    if (!btn || _isIconBtn(btn)) return '';
    var el = _textEl(btn);
    return (el ? el.textContent : btn.textContent).trim();
  }

  function _setBtnText(btn, text) {
    // Icon-only ATC: keep the SVG — busy state uses .is-loading dim only
    if (!btn || _isIconBtn(btn)) return;
    var el = _textEl(btn);
    if (el) el.textContent = text; else btn.textContent = text;
  }

  function _setBtnBusy(btn, busy) {
    if (!btn) return;
    btn.disabled = busy;
    btn.setAttribute('aria-busy', busy ? 'true' : 'false');
    btn.classList[busy ? 'add' : 'remove']('is-loading');
  }

  // ── Core addItem — direct fetch to Shopify Cart API ───────────────────────
  /**
   * @param {object} opts
   * @param {number|string} opts.id              — Shopify variant ID
   * @param {number|string} [opts.selling_plan]  — selling plan ID
   * @param {number}        [opts.quantity]       — defaults to 1
   * @param {number}        [opts.compare_at_cents] — stored as _compare_at_cents property
   * @param {object}        [opts.properties]    — additional cart line properties
   * @param {HTMLElement}   [opts.button]        — ATC button (manages its own loading state)
   * @param {Function}      [opts.onSuccess]
   * @param {Function}      [opts.onError]
   */
  function addItem(opts) {
    var id     = opts.id;
    var plan   = opts.selling_plan || '';
    var qty    = opts.quantity || 1;
    var catc   = opts.compare_at_cents || 0;
    var btn    = opts.button || null;
    var silent = opts.silent || false; // true = skip openDrawer (e.g. add-ons quiet add)
    var props  = Object.assign({}, opts.properties || {});
    var onOk   = opts.onSuccess;
    var onErr  = opts.onError;

    if (!id) {
      showToast('Please select a product option before adding to cart.', 'error');
      if (typeof onErr === 'function') onErr(new Error('No variant ID'));
      return;
    }

    if (catc) props._compare_at_cents = String(catc);

    var origText = getBtnText(btn);
    var btnGroup = _buttonsInAtcSyncGroup(btn);
    btnGroup.forEach(function (b) { _setBtnBusy(b, true); });
    btnGroup.forEach(function (b) { _setBtnText(b, 'Adding to cart'); });

    var payload = { id: +id, quantity: qty };
    var fbp = _getCookie('_fbp');
    if (fbp) payload.attributes = { fbp: fbp };
    if (plan) payload.selling_plan = +plan;
    if (Object.keys(props).length) payload.properties = props;

    (async function () {
      try {
        var res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify(payload)
        });
        if (!res.ok) {
          var e = await res.json();
          throw new Error(e.description || e.message || 'Could not add to cart.');
        }
        var data = await res.json();
        _dispatchEvents(id, qty, data);
        if (win.NNHCartCount && typeof win.NNHCartCount.sync === 'function') {
          win.NNHCartCount.sync();
        }
        btnGroup.forEach(function (b) { _setBtnText(b, 'Added to cart'); });
        setTimeout(function () {
          btnGroup.forEach(function (b) { _setBtnBusy(b, false); });
          btnGroup.forEach(function (b) { _setBtnText(b, origText); });
        }, 1200);
        if (!silent) openDrawer();
        if (typeof onOk === 'function') onOk(data);
      } catch (err) {
        btnGroup.forEach(function (b) { _setBtnBusy(b, false); });
        btnGroup.forEach(function (b) { _setBtnText(b, origText); });
        showToast(err.message || 'Failed to add to cart. Please try again.', 'error');
        if (typeof onErr === 'function') onErr(err);
      }
    }());
  }

  // ── addViaSkio — delegate to Skio's addToCart, then patch line properties ─
  /**
   * Call AFTER setting skioComponent.selectedVariant to the correct variant.
   *
   * @param {Element} skioComponent  — <skio-plan-picker> element
   * @param {object}  opts
   * @param {number|string} opts.id              — variant ID (for post-add lookup)
   * @param {number}        [opts.quantity]
   * @param {number}        [opts.compare_at_cents]
   * @param {object}        [opts.properties]    — extra properties to merge into line
   * @param {HTMLElement}   [opts.button]
   * @param {Function}      [opts.onSuccess]
   */
  function addViaSkio(skioComponent, opts) {
    var id    = opts.id;
    var qty   = opts.quantity || 1;
    var catc  = opts.compare_at_cents || 0;
    var extra = Object.assign({}, opts.properties || {});
    var btn   = opts.button || null;
    var onOk  = opts.onSuccess;

    var origText = getBtnText(btn);
    var btnGroup = _buttonsInAtcSyncGroup(btn);
    btnGroup.forEach(function (b) { _setBtnBusy(b, true); });
    btnGroup.forEach(function (b) { _setBtnText(b, 'Adding to cart'); });

    var settled = false;

    function onAdded(e) {
      if (settled) return;
      settled = true;
      skioComponent.removeEventListener('skio::added-to-cart', onAdded);

      // Open drawer immediately — don't block on property patch
      _dispatchEvents(id, qty, e.detail && e.detail.response);
      if (win.NNHCartCount && typeof win.NNHCartCount.sync === 'function') {
        win.NNHCartCount.sync();
      }
      btnGroup.forEach(function (b) { _setBtnText(b, 'Added to cart'); });
      setTimeout(function () {
        btnGroup.forEach(function (b) { _setBtnBusy(b, false); });
        btnGroup.forEach(function (b) { _setBtnText(b, origText); });
      }, 1200);
      openDrawer();
      if (typeof onOk === 'function') onOk();

      // Fire-and-forget: patch compare-at + extra properties in background
      if ((catc || Object.keys(extra).length) && id) {
        (async function () {
          try {
            var r = await fetch('/cart.js');
            var cart = await r.json();
            var line = cart.items && cart.items.find(function (i) {
              return String(i.id) === String(id);
            });
            if (!line) return;
            var merged = Object.assign({}, line.properties || {}, extra);
            if (catc) merged._compare_at_cents = String(catc);
            await fetch('/cart/change.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ id: line.key, properties: merged })
            });
          } catch (_) {}
        }());
      }
    }

    skioComponent.addEventListener('skio::added-to-cart', onAdded);

    // Safety: if Skio never fires, clean up after 10 s
    setTimeout(function () {
      if (settled) return;
      settled = true;
      skioComponent.removeEventListener('skio::added-to-cart', onAdded);
      btnGroup.forEach(function (b) { _setBtnBusy(b, false); });
      btnGroup.forEach(function (b) { _setBtnText(b, origText); });
    }, 10000);

    skioComponent.addToCart(qty);
  }

  // ── Event delegation for [data-nnh-atc] ──────────────────────────────────
  // Used by the Modular PDP (form + hidden inputs pattern).
  // Universal PDP calls NNHCart.addItem() / addViaSkio() directly.
  doc.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-nnh-atc]');
    if (!btn || btn.disabled || btn.getAttribute('aria-busy') === 'true') return;
    e.preventDefault();

    var form = btn.closest('[data-nnh-form]');
    if (!form) return;

    var purchaseRoot = btn.closest('[data-pcos-hero]') || form;
    var isModularHero = !!btn.closest('[data-pcos-hero]');

    var variantInput  = form.querySelector('[data-nnh-variant-id]');
    var planInput     = form.querySelector('[data-nnh-selling-plan]');
    var qtyInput      = form.querySelector('[data-nnh-quantity]');
    var catcInput     = form.querySelector('[data-nnh-compare-at]');

    var id   = variantInput && variantInput.value ? variantInput.value.trim() : null;
    var plan = planInput  ? planInput.value.trim()  : '';
    var qty  = qtyInput   ? (+qtyInput.value || 1)  : 1;
    var catc = catcInput  ? (+catcInput.value || 0)  : 0;

    if (!id) {
      showToast('Please select a product option before adding to cart.', 'error');
      return;
    }

    if (form.getAttribute('data-nnh-direct-checkout') === 'true') {
      var checkoutPlan = plan;
      var checkoutUrl = '/cart/clear?return_to=' + encodeURIComponent(
        '/cart/add?items[0][id]=' + id +
        (checkoutPlan ? '&items[0][selling_plan]=' + checkoutPlan : '') +
        '&items[0][quantity]=' + qty +
        '&return_to=/checkout'
      );
      win.location.href = checkoutUrl;
      return;
    }

    // Frequency radios live outside [data-nnh-form] on modular PDP — scope to hero.
    // Homepage / other forms have no toggle: keep the selling_plan already on the form.
    var subscribeInput = purchaseRoot.querySelector('[data-frequency-input][value="subscribe"]');
    var hasFrequencyToggle = !!purchaseRoot.querySelector('[data-frequency-input]');
    var isSubscribed = !!(subscribeInput && subscribeInput.checked);

    var props = {};
    if (typeof win.getSelectedFlavor === 'function') {
      var selectedFlavor = win.getSelectedFlavor();
      if (selectedFlavor) props.Flavor = selectedFlavor;
    }

    var skioComponent = doc.querySelector('skio-plan-picker');

    if (isSubscribed && skioComponent && !isModularHero && typeof skioComponent.addToCart === 'function') {
      if (!plan) {
        showToast('No delivery plan available for the selected option.', 'error');
        return;
      }
      // Sync Skio to the correct variant before delegating
      if (skioComponent.product) {
        var correctVariant = skioComponent.product.variants.find(function (v) {
          return String(v.id) === String(id);
        });
        if (correctVariant && skioComponent.selectedVariant &&
            skioComponent.selectedVariant.id !== correctVariant.id) {
          skioComponent.selectedVariant = correctVariant;
          if (typeof skioComponent.requestUpdate === 'function') skioComponent.requestUpdate();
        }
      }
      addViaSkio(skioComponent, { id: id, quantity: qty, compare_at_cents: catc, properties: props, button: btn });
      return;
    }

    if (hasFrequencyToggle && isSubscribed && !plan) {
      showToast('No delivery plan available for the selected option.', 'error');
      return;
    }

    if (hasFrequencyToggle && !isSubscribed) plan = '';

    addItem({ id: id, selling_plan: plan, quantity: qty, compare_at_cents: catc, properties: props, button: btn });
  });

  // ── Public API ────────────────────────────────────────────────────────────
  win.NNHCart = {
    addItem:     addItem,
    addViaSkio:  addViaSkio,
    openDrawer:  openDrawer,
    showToast:   showToast
  };

}(window, document));
